import { useId } from 'react';
import { colorHex, type BodyType } from '../lib/vehicle-catalog';

interface Shape {
  /** Silueta de la carrocería con los pasos de rueda recortados. */
  body: string;
  /** Ventanas (de adelante hacia atrás). */
  glass: string[];
  /** Líneas de puertas: [x, yArriba, yAbajo]. */
  seams: Array<[number, number, number]>;
  handles: Array<[number, number]>;
  /** Centro de las ruedas (x) y su altura (y), radio de la llanta. */
  wheels: [number, number];
  wy: number;
  r: number;
  belt: number;
  head: string;
  tail: string;
  mirror: string;
  extra?: string[];
  /** Molduras plásticas negras (SUV y pickup). */
  cladding?: boolean;
}

/** Siluetas laterales detalladas por tipo de carrocería (vista lateral, frente a la izquierda). */
const SHAPES: Record<'SEDAN' | 'SUV' | 'HATCHBACK' | 'PICKUP' | 'VAN', Shape> = {
  SEDAN: {
    body: 'M30 118 L30 104 C30 96 34 90 44 88 L92 80 C120 76 140 72 158 70 C176 56 196 44 222 42 L262 42 C290 43 312 56 330 70 L360 74 C370 76 374 84 374 92 L374 110 C374 116 370 118 364 118 L338 118 A30 30 0 0 0 278 118 L122 118 A30 30 0 0 0 62 118 Z',
    glass: ['M171 71 C185 58 200 50 222 49 L240 49 L240 71 Z', 'M247 49 L258 49 C278 50 296 59 311 71 L247 71 Z'],
    seams: [[172, 74, 114], [244, 46, 116], [316, 72, 114]],
    handles: [[214, 84], [284, 84]],
    wheels: [92, 308],
    wy: 112,
    r: 24,
    belt: 78,
    head: 'M33 93 C40 88 54 86 68 85 L64 92 C54 93 42 95 33 96 Z',
    tail: 'M358 75 L372 80 C374 84 374 88 374 92 L360 88 Z',
    mirror: 'M162 72 C164 66 170 64 178 65 L179 72 Z',
  },
  HATCHBACK: {
    body: 'M34 118 L34 104 C34 96 38 90 48 88 L96 80 C122 76 140 72 156 70 C172 54 190 42 214 40 L286 40 C300 40 310 46 320 58 L334 78 C340 86 342 94 342 102 L342 110 C342 116 338 118 332 118 L320 118 A30 30 0 0 0 260 118 L122 118 A30 30 0 0 0 62 118 Z',
    glass: ['M167 71 C181 56 195 48 214 47 L238 47 L238 71 Z', 'M245 47 L284 47 C294 47 302 52 310 62 L316 71 L245 71 Z'],
    seams: [[168, 74, 114], [242, 44, 116], [314, 72, 114]],
    handles: [[210, 84], [276, 84]],
    wheels: [92, 290],
    wy: 112,
    r: 24,
    belt: 78,
    head: 'M37 93 C44 88 58 86 70 85 L66 92 C56 93 46 95 37 96 Z',
    tail: 'M326 76 L336 80 C340 86 341 92 341 96 L330 90 Z',
    mirror: 'M160 72 C162 66 168 64 176 65 L177 72 Z',
  },
  SUV: {
    body: 'M26 116 L26 74 C26 66 31 62 40 61 L104 58 C126 57 142 57 152 56 C164 44 180 35 200 34 L330 34 C340 34 346 39 350 47 L356 62 C367 66 374 74 374 86 L374 108 C374 114 370 116 364 116 L336 116 A32 32 0 0 0 272 116 L128 116 A32 32 0 0 0 64 116 Z',
    glass: ['M161 61 C172 48 185 41 200 40 L228 40 L228 61 Z', 'M235 40 L284 40 L284 61 L235 61 Z', 'M291 40 L326 40 C334 40 339 44 342 50 L346 61 L291 61 Z'],
    seams: [[162, 64, 112], [232, 38, 114], [288, 38, 114]],
    handles: [[206, 72], [266, 72]],
    wheels: [96, 304],
    wy: 110,
    r: 26,
    belt: 66,
    head: 'M27 66 C34 63 48 62 62 62 L58 69 C48 70 36 71 27 72 Z',
    tail: 'M354 63 L370 69 C372 73 373 77 373 81 L356 78 Z',
    mirror: 'M152 61 C154 54 160 52 170 53 L171 61 Z',
    extra: ['M196 31 L326 31'],
    cladding: true,
  },
  PICKUP: {
    body: 'M26 116 L26 74 C26 66 31 62 40 61 L104 58 C122 57 134 57 144 56 C156 44 168 35 186 34 L236 34 C244 34 248 38 250 46 L252 64 L372 64 L374 70 L374 108 C374 114 370 116 364 116 L338 116 A32 32 0 0 0 274 116 L124 116 A32 32 0 0 0 60 116 Z',
    glass: ['M153 61 C164 48 176 41 188 40 L206 40 L206 61 Z', 'M212 40 L236 40 C240 40 242 42 243 46 L245 61 L212 61 Z'],
    seams: [[154, 64, 112], [209, 38, 114], [250, 62, 114], [256, 64, 112]],
    handles: [[188, 72], [230, 72]],
    wheels: [92, 306],
    wy: 110,
    r: 26,
    belt: 66,
    head: 'M27 66 C34 63 48 62 62 62 L58 69 C48 70 36 71 27 72 Z',
    tail: 'M366 68 L374 68 L374 86 L366 86 Z',
    mirror: 'M144 61 C146 54 152 52 162 53 L163 61 Z',
    extra: ['M252 64 L372 64'],
    cladding: true,
  },
  VAN: {
    body: 'M22 116 L22 92 C22 84 26 78 36 76 L72 70 C86 52 104 34 130 30 L352 30 C366 30 374 38 374 52 L374 108 C374 114 370 116 364 116 L344 116 A32 32 0 0 0 280 116 L122 116 A32 32 0 0 0 58 116 Z',
    glass: ['M89 68 C101 52 115 40 134 37 L166 37 L166 68 Z', 'M174 37 L240 37 L240 68 L174 68 Z', 'M248 37 L314 37 L314 68 L248 68 Z', 'M322 37 L352 37 C360 37 366 42 366 50 L366 68 L322 68 Z'],
    seams: [[170, 34, 112], [244, 34, 114], [318, 34, 112]],
    handles: [[206, 80], [282, 80]],
    wheels: [90, 312],
    wy: 110,
    r: 26,
    belt: 72,
    head: 'M25 82 C32 78 44 76 56 75 L52 84 C42 85 32 86 25 87 Z',
    tail: 'M366 72 L374 72 L374 92 L366 92 Z',
    mirror: 'M80 70 C82 63 88 61 98 62 L99 70 Z',
    extra: ['M176 74 L312 74'],
  },
};

const shapeFor = (type: string | null | undefined): Shape => {
  if (type === 'SUV') return SHAPES.SUV;
  if (type === 'HATCHBACK') return SHAPES.HATCHBACK;
  if (type === 'PICKUP') return SHAPES.PICKUP;
  if (type === 'VAN' || type === 'MINIVAN') return SHAPES.VAN;
  return SHAPES.SEDAN;
};

/** Mezcla un color con blanco (t > 0) o negro (t < 0). */
function shade(hex: string, t: number) {
  const n = parseInt(hex.slice(1), 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => Math.round(t > 0 ? c + (255 - c) * t : c * (1 + t)));
  return `#${ch.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Ilustración lateral del vehículo según su tipo, pintada del color elegido.
 * Se usa mientras el conductor no haya subido fotos reales.
 */
export function VehicleIllustration({ type, color, className, title }: { type: BodyType | string | null | undefined; color: string | null | undefined; className?: string; title?: string }) {
  const uid = useId().replace(/:/g, '');
  const hex = color?.startsWith('#') ? color : colorHex(color);
  const s = shapeFor(type);
  const lum = (parseInt(hex.slice(1, 3), 16) * 299 + parseInt(hex.slice(3, 5), 16) * 587 + parseInt(hex.slice(5, 7), 16) * 114) / 1000;
  const dark = lum < 70;
  const light = lum > 200;
  const ids = { body: `vb${uid}`, glass: `vg${uid}`, rim: `vr${uid}`, clip: `vc${uid}`, tire: `vt${uid}` };
  const [w1, w2] = s.wheels;

  return (
    <svg viewBox="0 0 400 150" className={className} role="img" aria-label={title ?? 'Vehículo'}>
      <defs>
        <linearGradient id={ids.body} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={shade(hex, dark ? 0.32 : 0.28)} />
          <stop offset="0.3" stopColor={shade(hex, dark ? 0.12 : 0.06)} />
          <stop offset="0.55" stopColor={hex} />
          <stop offset="1" stopColor={shade(hex, light ? -0.22 : -0.45)} />
        </linearGradient>
        <linearGradient id={ids.glass} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#5c7387" />
          <stop offset="0.45" stopColor="#24313d" />
          <stop offset="1" stopColor="#0f161d" />
        </linearGradient>
        <radialGradient id={ids.rim} cx="0.4" cy="0.35" r="0.75">
          <stop offset="0" stopColor="#f2f4f6" />
          <stop offset="0.6" stopColor="#aeb4bb" />
          <stop offset="1" stopColor="#6b7178" />
        </radialGradient>
        <radialGradient id={ids.tire} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0.7" stopColor="#2a2a2a" />
          <stop offset="1" stopColor="#0c0c0c" />
        </radialGradient>
        <clipPath id={ids.clip}>
          <path d={s.body} />
        </clipPath>
      </defs>

      {/* Sombra en el piso */}
      <ellipse cx="200" cy={s.wy + s.r + 4} rx="178" ry="7" fill="#000" opacity="0.18" />
      {/* Interior oscuro de los pasos de rueda */}
      {[w1, w2].map((x) => (
        <circle key={`a${x}`} cx={x} cy={s.wy} r={s.r + 5} fill="#0d0d0d" />
      ))}

      {/* Carrocería */}
      <path d={s.body} fill={`url(#${ids.body})`} stroke={light ? '#00000033' : '#00000055'} strokeWidth="1" />
      <g clipPath={`url(#${ids.clip})`}>
        {/* Brillo de la línea de hombros y faldón inferior más oscuro */}
        <rect x="0" y={s.belt - 7} width="400" height="3" fill="#fff" opacity={dark ? 0.16 : 0.28} />
        <rect x="0" y={s.belt + 1} width="400" height="1.2" fill="#000" opacity="0.18" />
        <rect x="0" y={s.wy - 4} width="400" height="20" fill="#000" opacity="0.2" />
        {/* Puertas y manillas */}
        {s.seams.map(([x, y1, y2]) => (
          <line key={`s${x}`} x1={x} y1={y1} x2={x} y2={y2} stroke="#000" strokeOpacity="0.28" strokeWidth="1" />
        ))}
        {s.handles.map(([x, y]) => (
          <rect key={`h${x}`} x={x - 7} y={y} width="14" height="3" rx="1.5" fill={shade(hex, light ? -0.25 : 0.2)} stroke="#00000040" strokeWidth="0.5" />
        ))}
        {s.extra?.map((d) => <path key={d} d={d} stroke="#000" strokeOpacity="0.35" strokeWidth="2.5" fill="none" strokeLinecap="round" />)}
      </g>

      {s.cladding && (
        <g clipPath={`url(#${ids.clip})`}>
          <rect x="0" y={s.wy - 2} width="400" height="20" fill="#1b1b1b" opacity="0.85" />
          {[w1, w2].map((x) => (
            <circle key={`c${x}`} cx={x} cy={s.wy} r={s.r + 10} fill="none" stroke="#1b1b1b" strokeWidth="8" />
          ))}
        </g>
      )}

      {/* Ventanas con marco negro y reflejo */}
      {s.glass.map((d) => (
        <g key={d}>
          <path d={d} fill={`url(#${ids.glass})`} stroke="#0a0a0a" strokeWidth="2.6" strokeLinejoin="round" />
          <path d={d} fill="#fff" opacity="0.08" clipPath={`url(#${ids.clip})`} />
        </g>
      ))}

      {/* Espejo, luces */}
      <path d={s.mirror} fill={shade(hex, -0.2)} stroke="#0008" strokeWidth="0.8" />
      <path d={s.head} fill="#eef6ff" stroke="#9fb3c4" strokeWidth="0.8" />
      <path d={s.tail} fill="#c3121b" stroke="#7a0a10" strokeWidth="0.8" />

      {/* Ruedas: llanta, aro con rayos y tapón */}
      {[w1, w2].map((x) => (
        <g key={`w${x}`} transform={`translate(${x} ${s.wy})`}>
          <circle r={s.r} fill={`url(#${ids.tire})`} />
          <circle r={s.r - 3} fill="none" stroke="#3a3a3a" strokeWidth="1" />
          <circle r={s.r * 0.66} fill={`url(#${ids.rim})`} stroke="#555" strokeWidth="0.8" />
          {[0, 72, 144, 216, 288].map((a) => (
            <path key={a} d={`M-2.6 ${-s.r * 0.18} L-3.6 ${-s.r * 0.62} L3.6 ${-s.r * 0.62} L2.6 ${-s.r * 0.18} Z`} fill="#5d636a" transform={`rotate(${a})`} />
          ))}
          <circle r={s.r * 0.2} fill="#d9dde1" stroke="#555" strokeWidth="0.8" />
          <circle r={s.r * 0.07} fill="#666" />
        </g>
      ))}
    </svg>
  );
}
